/**
 * QR helpers: scan (native camera) + generate (data URL for display).
 */
import { Capacitor } from '@capacitor/core';
import QRCode from 'qrcode';

export function canScanQr(): boolean {
  return Capacitor.isNativePlatform();
}

/** Opens the native camera scanner; returns the first decoded value, or null. */
export async function scanQrCode(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const { BarcodeScanner } = await import('@capacitor-mlkit/barcode-scanning');
    const perm = await BarcodeScanner.requestPermissions();
    if (perm.camera !== 'granted' && perm.camera !== 'limited') return null;
    // Android downloads the scanner module on demand; ignore if unsupported.
    try {
      const avail = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
      if (!avail.available) await BarcodeScanner.installGoogleBarcodeScannerModule();
    } catch { /* iOS / already available */ }
    const { barcodes } = await BarcodeScanner.scan();
    return barcodes?.[0]?.rawValue ?? null;
  } catch {
    return null;
  }
}

/** Pulls a study-group room code out of a scanned URL or raw value. */
export function roomCodeFromScan(value: string): string | null {
  const m = value.match(/\/groups\/join\/([A-Za-z0-9]+)/);
  if (m) return m[1]!.toUpperCase();
  const raw = value.trim().toUpperCase();
  return /^[A-Z0-9]{4,8}$/.test(raw) ? raw : null;
}

export async function qrDataUrl(text: string): Promise<string> {
  return QRCode.toDataURL(text, { width: 240, margin: 1, color: { dark: '#1e1b4b', light: '#ffffff' } });
}
